import { Injectable } from '@angular/core';

import { ActivityDisplay, SlideDuration, Theme } from '../../shared/enums';
import { ConfigurationState } from '../../shared/interfaces';
import { ContentKind, FeedKind } from '../slides/enums';
import { CONFIG_DEFAULTS } from './config.defaults';
import { SignageConfigurationModel } from './models';
import { ConfigurationTelemetry } from './interfaces';

const MAX_CAROUSEL_SECONDS = 3600;
const HEX_COLOR = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

// Validates the raw channel into the model the runtime reads; every field defaults and reports that it did, so a bad channel still plays.
@Injectable({ providedIn: 'root' })
export class ConfigurationParserService {
  public parse(input: ConfigurationState, telemetry?: ConfigurationTelemetry): SignageConfigurationModel {
    const raw = input && typeof input === 'object' ? input : {} as ConfigurationState;
    const channel = raw.channel || { providerUuid: '' };
    const owner = raw.owner || { timezoneId: 'UTC', defaultCultureCode: 'en-US' };

    const timezone = this.validTimezone(owner.timezoneId, telemetry);
    const culture = this.validCulture(owner.defaultCultureCode, telemetry);
    const slideDuration = this.validSlideDuration(raw.slideDuration, telemetry);
    const activityDisplay = this.validActivityDisplay(channel.activityDisplay, telemetry);
    const carouselSecondsPerImage = this.validCarouselSeconds(raw.secondsPerImageInCarrousel, telemetry);
    const themeStyle = this.validThemeStyle(raw.themeStyle, telemetry);
    const primaryColor = this.validPrimaryColor(raw.primaryColor, telemetry);

    const normalized: ConfigurationState = {
      ...CONFIG_DEFAULTS, ...raw,
      channel: { ...CONFIG_DEFAULTS.channel, ...channel, activityDisplay },
      owner: { ...owner, timezoneId: timezone, defaultCultureCode: culture },
      slideDuration, secondsPerImageInCarrousel: carouselSecondsPerImage, themeStyle, primaryColor,
      serviceNames: raw.serviceNames || [], translationContext: raw.translationContext || ''
    };

    return {
      channel: { providerUuid: channel.providerUuid || '', id: channel.id ?? 0, imagesSet: channel.imagesSet },
      community: { timezone, culture, uuid: owner.uuid || owner.identityUuid || '' },
      enabled: this.enabledFeeds(raw, telemetry),
      raw: normalized, carouselSecondsPerImage, translationContext: normalized.translationContext,
      closedCaptions: raw.closedCaptions === true, activityDisplay, slideDuration,
      priorityMessages: this.priorityMessages(raw)
    };
  }

  private enabledFeeds(raw: ConfigurationState, telemetry?: ConfigurationTelemetry): Readonly<Record<FeedKind, boolean>> {
    const channel = raw.channel || { providerUuid: '' };

    return {
      [ContentKind.News]: this.enabled(channel.newsEnabled, 'newsEnabled', telemetry),
      [ContentKind.Birthdays]: this.enabled(channel.birthdaySlideEnabled, 'birthdaySlideEnabled', telemetry),
      [ContentKind.Dining]: this.enabled(channel.diningEnabled, 'diningEnabled', telemetry),
      [ContentKind.Activities]: this.enabled(channel.activitiesEnabled, 'activitiesEnabled', telemetry),
      [ContentKind.PriorityNews]: this.enabled(raw.arePrioMessagesEnabled, 'arePrioMessagesEnabled', telemetry),
      [ContentKind.YoutubeLive]: true,
      [ContentKind.ScheduledVideo]: true,
      [ContentKind.Weather]: true,
      [ContentKind.Mail]: true
    };
  }

  private priorityMessages(raw: ConfigurationState): { readonly enabled: boolean; readonly scrollSpeed: number } {
    const scrollSpeed = Number.isFinite(raw.priorityMessageScrollSpeed) && raw.priorityMessageScrollSpeed >= 0
      ? raw.priorityMessageScrollSpeed : 0;

    return { enabled: typeof raw.arePrioMessagesEnabled === 'boolean' ? raw.arePrioMessagesEnabled : true, scrollSpeed };
  }

  private validTimezone(timezone: string, telemetry?: ConfigurationTelemetry): string {
    try {
      if (!timezone) {
        throw new Error();
      }

      new Intl.DateTimeFormat('en-US', { timeZone: timezone });

      return timezone;
    } catch {
      this.report('timezone', telemetry);

      return 'UTC';
    }
  }

  private validCulture(culture: string, telemetry?: ConfigurationTelemetry): string {
    try {
      if (!culture) {
        throw new Error();
      }

      new Intl.DateTimeFormat(culture);

      return culture;
    } catch {
      this.report('culture', telemetry);

      return 'en-US';
    }
  }

  private validSlideDuration(duration: SlideDuration, telemetry?: ConfigurationTelemetry): SlideDuration {
    if ([SlideDuration.Short, SlideDuration.Medium, SlideDuration.Long].includes(duration)) {
      return duration;
    }

    this.report('slideDuration', telemetry);

    return SlideDuration.Medium;
  }

  private validActivityDisplay(display: ActivityDisplay, telemetry?: ConfigurationTelemetry): ActivityDisplay {
    if ([ActivityDisplay.Both, ActivityDisplay.List, ActivityDisplay.DetailPages].includes(display)) {
      return display;
    }

    this.report('activityDisplay', telemetry);

    return ActivityDisplay.Both;
  }

  // The legacy flat designs were never built for any client, so only their luminosity carries the
  // community's intent; they alias onto the wave themes rather than being defaulted away.
  private validThemeStyle(theme: Theme | undefined, telemetry?: ConfigurationTelemetry): Theme {
    switch (theme) {
      case Theme.WaveDark:
      case Theme.Dark:
        return Theme.WaveDark;
      case Theme.WaveLight:
      case Theme.Light:
        return Theme.WaveLight;
      default:
        this.report('themeStyle', telemetry);

        return Theme.WaveDark;
    }
  }

  // Reaches the DOM as a custom property value, which Angular does not sanitise.
  private validPrimaryColor(color: string, telemetry?: ConfigurationTelemetry): string {
    if (typeof color === 'string' && HEX_COLOR.test(color)) {
      return color;
    }

    this.report('primaryColor', telemetry);

    return CONFIG_DEFAULTS.primaryColor;
  }

  private validCarouselSeconds(value: number, telemetry?: ConfigurationTelemetry): number {
    const seconds = Number(value);

    if (Number.isFinite(seconds) && seconds > 0 && seconds <= MAX_CAROUSEL_SECONDS) {
      return seconds;
    }

    this.report('secondsPerImageInCarrousel', telemetry);

    return CONFIG_DEFAULTS.secondsPerImageInCarrousel;
  }

  private enabled(value: unknown, field: string, telemetry?: ConfigurationTelemetry): boolean {
    if (typeof value === 'boolean') {
      return value;
    }

    this.report(field, telemetry);

    return true;
  }

  private report(field: string, telemetry?: ConfigurationTelemetry): void {
    telemetry?.trackEvent('configuration_defaulted', { field });
  }
}
